<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">邮箱账号管理</h2>
        <p class="page-subtitle">管理用户账号及其凭据</p>
      </div>
      <a-button type="primary" class="action-btn" @click="showModal = true">
        <template #icon><plus-outlined /></template>
        新建账号
      </a-button>
    </div>

    <a-card class="data-card" :bordered="false">
      <div class="filter-bar">
        <a-input-search
          v-model:value.trim="searchText"
          placeholder="搜索手机号或邮箱"
          allow-clear
          size="large"
          class="filter-input"
          @search="handleSearch"
          @press-enter="handleSearch"
          @change="debounceSearch"
        />
        <a-button type="link" @click="handleReset">重置</a-button>
      </div>
      <a-table
        :dataSource="accounts"
        :columns="columns"
        :loading="loading"
        :pagination="paginationConfig"
        rowKey="id"
        class="custom-table"
      >
        <template #bodyCell="{ column, text }">
          <template v-if="column.dataIndex === 'created_at'">
            <span class="date-text">{{ formatDate(text) }}</span>
          </template>
          <template v-else-if="column.dataIndex === 'id'">
            <span class="id-text">#{{ text }}</span>
          </template>
          <template v-else>
            <a-tooltip placement="top" title="点击复制">
              <span
                class="copyable-text"
                @click="copyToClipboard(text, $event)"
              >
                {{ text }}
                <copy-outlined class="copy-icon" />
              </span>
            </a-tooltip>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:visible="showModal"
      title="创建新账号"
      @ok="handleCreate"
      :confirmLoading="creating"
      class="custom-modal"
      okText="确认创建"
      cancelText="取消"
    >
      <div class="modal-header-accent"></div>
      <a-form :model="form" layout="vertical" class="custom-form">
        <a-form-item label="手机号" required>
          <a-input v-model:value="form.phone" size="large" placeholder="请输入手机号"
            @input="form.phone = form.phone.replace(/\D/g, '')" />
        </a-form-item>
        <a-form-item label="邮箱地址" required>
          <div style="display: flex; align-items: center; gap: 4px;">
            <a-input v-model:value="form.email" size="large" placeholder="输入用户名"
              @input="form.email = form.email.replace(/[^a-zA-Z0-9]/g, '')"
              style="flex: 0 0 auto; min-width: 0; max-width: 300px;" />
            <span style="color: var(--color-slate-light); white-space: nowrap; user-select: none; font-size: 14px;">@virexomail.com</span>
          </div>
        </a-form-item>
        <a-form-item label="邮箱密码">
          <a-input v-model:value="form.raw_password" size="large" placeholder="默认: a123456@78" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import api from '../api';
import { message } from 'ant-design-vue';
import { PlusOutlined, CopyOutlined } from '@ant-design/icons-vue';

export default {
  components: {
    PlusOutlined,
    CopyOutlined
  },
  setup() {
    const accounts = ref([]);
    const loading = ref(false);
    const showModal = ref(false);
    const creating = ref(false);
    const searchText = ref('');
    const filterPhone = ref('');
    const filterEmail = ref('');
    const current = ref(1);
    const pageSize = ref(10);
    const total = ref(0);

    const paginationConfig = computed(() => ({
      current: current.value,
      pageSize: pageSize.value,
      total: total.value,
      showSizeChanger: true,
      showTotal: (t) => `共 ${t} 条`,
      onChange: (page, size) => {
        current.value = page;
        pageSize.value = size;
        fetchAccounts();
      },
      onShowSizeChange: (_, size) => {
        pageSize.value = size;
        current.value = 1;
        fetchAccounts();
      },
    }));

    const columns = [
      { title: 'ID', dataIndex: 'id', width: '80px' },
      { title: '手机号', dataIndex: 'phone' },
      { title: '邮箱', dataIndex: 'email' },
      { title: '邮箱密码', dataIndex: 'email_pass' },
      { title: '创建时间', dataIndex: 'created_at' },
    ];

    const fetchAccounts = async () => {
      loading.value = true;
      try {
        const params = { page: current.value, pageSize: pageSize.value };
        if (filterPhone.value) params.phone = filterPhone.value;
        if (filterEmail.value) params.email = filterEmail.value;
        const res = await api.get('/accounts', { params });
        accounts.value = res.data.list || res.data;
        total.value = res.data.total || res.data.list?.length || res.data.length || 0;
      } catch (err) {
        message.error('获取账号列表失败');
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      const parts = searchText.value.split(/\s+/);
      filterPhone.value = parts[0] || '';
      filterEmail.value = parts[1] || '';
      current.value = 1;
      fetchAccounts();
    };

    const debounceTimeout = ref(null);
    const debounceSearch = () => {
      clearTimeout(debounceTimeout.value);
      debounceTimeout.value = setTimeout(handleSearch, 400);
    };

    const handleReset = () => {
      searchText.value = '';
      filterPhone.value = '';
      filterEmail.value = '';
      current.value = 1;
      fetchAccounts();
    };

    const handleCreate = async () => {
      if (!form.phone || !form.email) {
        return message.warning('请填写必填项');
      }
      creating.value = true;
      try {
        await api.post('/accounts', form);
        message.success('账号创建成功');
        showModal.value = false;
        form.phone = '';
        form.email = '';
        form.raw_password = 'a123456@78';
        fetchAccounts();
      } catch (err) {
        message.error('创建失败: ' + (err.response?.data?.error || err.message));
      } finally {
        creating.value = false;
      }
    };

    const copyToClipboard = (text, event) => {
      if (!text) return;
      const el = event.currentTarget;
      el.classList.add('copied-pulse');
      setTimeout(() => el.classList.remove('copied-pulse'), 600);
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text.toString()).then(() => {
          message.success('已复制到剪贴板');
        }).catch(() => {
          fallbackCopy(text);
        });
      } else {
        fallbackCopy(text);
      }
    };

    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text.toString();
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('已复制到剪贴板');
      } catch (err) {
        message.error('复制失败');
      }
      document.body.removeChild(textArea);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const form = reactive({ phone: '', email: '', raw_password: 'a123456@78' });

    onMounted(fetchAccounts);

    return {
      accounts,
      columns,
      showModal,
      form,
      handleCreate,
      creating,
      copyToClipboard,
      formatDate,
      loading,
      paginationConfig,
      searchText,
      handleSearch,
      debounceSearch,
      handleReset,
    };
  },
};
</script>

<style scoped>
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--color-navy);
  letter-spacing: -0.5px;
}

.page-subtitle {
  margin: 4px 0 0 0;
  color: var(--color-slate);
  font-size: 14px;
}

.action-btn {
  height: 40px;
  padding: 0 20px;
  border-radius: 6px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  background-color: #3b82f6;
  border-color: #3b82f6;
  transition: all 0.2s;
}

.action-btn:hover {
  background-color: #2563eb;
  border-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
}

.data-card {
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.filter-bar {
  padding: 16px 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-input {
  width: 300px;
}

.page-header .filter-bar {
  padding: 0;
  margin-bottom: 16px;
}

/* Table Styling */
:deep(.custom-table .ant-table-thead > tr > th) {
  background-color: #F8FAFC;
  color: var(--color-slate);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--color-border);
}

:deep(.custom-table .ant-table-tbody > tr > td) {
  border-bottom: 1px solid var(--color-border);
  color: var(--color-navy-light);
}

:deep(.custom-table .ant-table-tbody > tr:hover > td) {
  background-color: #F1F5F9;
}

.id-text {
  color: var(--color-slate-light);
  font-family: monospace;
  font-size: 13px;
}

.date-text {
  color: var(--color-slate);
  font-size: 13px;
}

.copyable-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 4px;
  transition: all 0.2s;
  font-family: monospace;
  font-size: 14px;
  color: var(--color-navy-light);
}

.copy-icon {
  opacity: 0;
  color: #3b82f6;
  transition: opacity 0.2s;
  font-size: 12px;
}

.copyable-text:hover {
  background-color: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

.copyable-text:hover .copy-icon {
  opacity: 1;
}

@keyframes pulse-blue {
  0% { background-color: rgba(59, 130, 246, 0.2); }
  50% { background-color: rgba(59, 130, 246, 0.4); }
  100% { background-color: transparent; }
}

.copied-pulse {
  animation: pulse-blue 0.6s ease-out;
}

/* Modal Styling */
:deep(.custom-modal .ant-modal-content) {
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
}

:deep(.custom-modal .ant-modal-header) {
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
}

:deep(.custom-modal .ant-modal-title) {
  font-weight: 600;
  color: var(--color-navy);
}

:deep(.custom-modal .ant-modal-body) {
  padding: 24px;
}

:deep(.custom-modal .ant-modal-footer) {
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  background-color: #F8FAFC;
}

.modal-header-accent {
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.custom-form :deep(.ant-form-item-label > label) {
  font-weight: 500;
  color: var(--color-navy-light);
}

.custom-form :deep(.ant-input) {
  border-radius: 6px;
}

.custom-form :deep(.ant-input:focus),
.custom-form :deep(.ant-input-focused) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}
</style>
